import React, {createContext, useCallback, useContext, useMemo, useRef} from 'react';
import {type SharedValue, useSharedValue} from 'react-native-reanimated';

export const TAB_BAR_HEIGHT = 52;

/** Union of scroll methods across ScrollView, FlatList, and FlashList */
export type ScrollableRef = {
  scrollTo?: (options: {x?: number; y?: number; animated?: boolean}) => void;
  scrollToOffset?: (options: {offset: number; animated?: boolean}) => void;
  scrollToIndex?: (options: {index: number; animated?: boolean}) => void;
} | null;

type ScrollCollapseContextValue = {
  scrollY: SharedValue<number>;
  prevScrollY: SharedValue<number>;
  accumulatedUp: SharedValue<number>;
  accumulatedDown: SharedValue<number>;
  /** Measured header height — used by StickyFilterBar to compensate for header collapse */
  headerHeight: SharedValue<number>;
  /**
   * DIS-1840: how far the visible bottom of `SiteHeader` has moved up — 0 at full height,
   * `full − compact` in compact. Written only by `useCompactHeader`; read by the overlay rows
   * only while `isSiteHeaderActive()`. Native and HEADER-denied web never read it.
   */
  siteHeaderCompactOffset: SharedValue<number>;
  TAB_BAR_HEIGHT: number;
};

/**
 * Separate context for the active scroll ref.
 *
 * IMPORTANT: This MUST be a separate context from the SharedValue context.
 * When components capture the SharedValue context in Reanimated worklets
 * (e.g. useAnimatedStyle), Reanimated freezes the entire context object.
 * If activeScrollRef lives in that same object, `.current` mutations are
 * silently blocked — causing scroll-to-top to target a stale/detached list.
 */
type ScrollRefContextValue = {
  activeScrollRef: React.MutableRefObject<React.RefObject<unknown> | null>;
  setActiveScrollRef: (ref: React.RefObject<unknown>) => void;
};

const ScrollCollapseContext = createContext<ScrollCollapseContextValue | null>(null);
const ScrollRefContext = createContext<ScrollRefContextValue | null>(null);

export function ScrollCollapseProvider({children}: {children: React.ReactNode}) {
  const scrollY = useSharedValue(0);
  const prevScrollY = useSharedValue(0);
  const accumulatedUp = useSharedValue(TAB_BAR_HEIGHT); // start visible
  const accumulatedDown = useSharedValue(0);
  const headerHeight = useSharedValue(84); // default fallback, measured by Header onLayout
  const siteHeaderCompactOffset = useSharedValue(0);
  const activeScrollRef = useRef<React.RefObject<unknown> | null>(null);

  const setActiveScrollRef = useCallback((ref: React.RefObject<unknown>) => {
    activeScrollRef.current = ref;
  }, []);

  const collapseValue = useMemo(
    () => ({
      scrollY,
      prevScrollY,
      accumulatedUp,
      accumulatedDown,
      headerHeight,
      siteHeaderCompactOffset,
      TAB_BAR_HEIGHT,
    }),
    [scrollY, prevScrollY, accumulatedUp, accumulatedDown, headerHeight, siteHeaderCompactOffset],
  );

  const refValue = useMemo(() => ({activeScrollRef, setActiveScrollRef}), [setActiveScrollRef]);

  return (
    <ScrollCollapseContext.Provider value={collapseValue}>
      <ScrollRefContext.Provider value={refValue}>{children}</ScrollRefContext.Provider>
    </ScrollCollapseContext.Provider>
  );
}

/** Strict version — throws when outside provider */
export function useScrollCollapseContext() {
  const ctx = useContext(ScrollCollapseContext);
  if (!ctx) {
    throw new Error('useScrollCollapseContext must be used within ScrollCollapseProvider');
  }
  return ctx;
}

/** Safe version — returns null when outside provider (for shared components) */
export function useOptionalScrollCollapseContext() {
  return useContext(ScrollCollapseContext);
}

/** Access the active scroll ref (for scroll-to-top). Separate from worklet context. */
export function useScrollRefContext() {
  const ctx = useContext(ScrollRefContext);
  if (!ctx) {
    throw new Error('useScrollRefContext must be used within ScrollCollapseProvider');
  }
  return ctx;
}

/** Safe version — returns null when outside provider */
export function useOptionalScrollRefContext() {
  return useContext(ScrollRefContext);
}
